import { useMutation } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { type EnqueueSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import { isValidUUID } from '@/features/SourceCreate/SourceCreate.utils';
import devicesAtom, {
  CustomEventTypes,
  cloudSourcesAtom,
  customEventAtom,
  enableFetchObjectsAtom,
  enableVulScanAtom,
  selectedCloudSourceAtom,
  selectedCloudSourceRowsAtom,
  selectedDeviceAtom,
  selectedDeviceIdAtom,
  selectedSourceAtom,
  sourceStatusAtom
} from '@/pages/Administration/Devices/Devices.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient, queryClient } from '@/services/BaseApi.service';

import SOURCE_TYPE from './SourceCreate.constants';
import { downloadCertificate } from './SourceCreate.service';
import {
  authenticationModeAtom,
  categoryAtom,
  connectionInputSchemaAtom,
  deviceAlertFieldsAtom,
  limaDevicesAtom,
  newCertificateAtom,
  regenerateCertificateAtom,
  selectedCategoryAtom,
  selectedCloudProviderAtom,
  selectedIngestConfigTabAtom,
  selectedRegionAtom,
  selectedSourceDetailsAtom,
  selectedSourceTypeIdAtom,
  selectedSubSourceTypeAtom,
  selectedSubscriptionTypeAtom,
  selectedSubTimeIntervalAtom,
  selectedTimeIntervalAtom,
  selectedVulnFieldsAtom,
  sourceCreateRequestAtom,
  sourceDialogModeAtom,
  sourceDialogOpenAtom,
  sourceLogTypeAtom,
  sourceResponseAtom,
  sourceTypeBasicAtom,
  subTimeIntervalOptionsAtom,
  wizardCurrentStepAtom,
  wizardSelectedSourceAtom
} from './SourceCreate.store';
import {
  type MedigateOndemandIngestionDTO,
  type ResubmitCustomObjectRequest,
  type SourceCreateBaseDTO,
  type SourceDetailResponse,
  SourceDialogMode,
  type ToggleActiveStatePayload
} from './SourceCreate.types';

/**
 * Maps source dialog modes to user-friendly success messages.
 * Standard pattern: action message + "Successfully"
 */
const showSnackbar = (mode: string, enqueueSnackbar: EnqueueSnackbar) => {
  const modeToMessage: Record<string, string> = {
    Edit: 'Source  Updated',
    AddThumbPrintId: 'Thumbprint Id Updated',
    AccessCode: 'Access Code Id Updated',
    Delete: 'Source Delete Initiated',
    Resubmit: 'Resubmit initiated',
    Runingestion: 'Runingestion initiated',
    ResubmitCustomObject: 'Resubmit custom object initiated',
    activated: 'Activated Sources',
    deactivated: 'Deactivated Sources'
  };

  const action = modeToMessage[mode] || 'Source  Saved';
  enqueueSnackbar(`${action} Successfully`, { variant: 'success' });
};

/**
 * Primary hook for managing source creation/editing dialog and related operations.
 *
 * Responsibilities:
 * - Dialog lifecycle (open/close, mode transitions)
 * - Source CRUD mutations (create, edit, delete)
 * - Special workflows (certificate download, thumbprint ID, access code)
 * - Source resubmission and ingestion triggers
 * - Bulk activation/deactivation operations
 *
 * Key flows:
 * 1. New Source: Dialog opens → User fills form → Submit → Success/Error handling
 * 2. Edit Source: Opens with pre-filled data → Modify → Update
 * 3. Certificate Auth: Create → Download cert → Add thumbprint ID → Complete
 * 4. Dropbox: Create → Add access code → Complete
 */
export const useSourceDialog = () => {
  const [error, setError] = useState('');
  const [mode, setMode] = useAtom(sourceDialogModeAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useAtom(sourceDialogOpenAtom);
  const [sourceRequest, setSourceRequest] = useAtom(sourceCreateRequestAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);
  const [selectedCloudSource] = useAtom(selectedCloudSourceAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);

  const [authenticationMode, setAuthenticationMode] = useAtom(
    authenticationModeAtom
  );
  const [regenerateCertificate, setRegenerateCertificate] = useAtom(
    regenerateCertificateAtom
  );

  const [newCertificate, setNewCertificate] = useAtom(newCertificateAtom);
  const [sourceResponse, setSourceResponse] = useAtom(sourceResponseAtom);
  const [selectedSourceTypeId, setSelectedSourceTypeId] = useAtom(
    selectedSourceTypeIdAtom
  );
  const [selectedSourceDetails, setSelectedSourceDetails] = useAtom(
    selectedSourceDetailsAtom
  );
  const [, setConnectionInputSchema] = useAtom(connectionInputSchemaAtom);
  const [, setDeviceId] = useAtom(selectedDeviceIdAtom);
  const [, setSelectedIngestConfigTab] = useAtom(selectedIngestConfigTabAtom);
  const [cloudSourcesData, setCloudSourcesData] = useAtom(cloudSourcesAtom);
  const [, setSelectedCloudSourceRows] = useAtom(selectedCloudSourceRowsAtom);
  const [, setCurrentStep] = useAtom(wizardCurrentStepAtom);
  const [, setWizardSelectedSource] = useAtom(wizardSelectedSourceAtom);

  /**
   * Clears all source selections in the activation/deactivation grid.
   *
   * Why needed: After bulk activation/deactivation operations, we must clear
   * the is_selected flag on all sources to reset the UI state. This prevents
   * stale selections from persisting after the operation completes.
   */
  const resetSourceActivationSelection = useCallback(() => {
    // Clear selection state used by bulk activation toolbar.
    setSelectedCloudSourceRows([]);

    // Update atom state - clear all is_selected flags
    const updatedSources = cloudSourcesData.map((source) => ({
      ...source,
      is_selected: false
    }));

    setCloudSourcesData(updatedSources);
  }, [cloudSourcesData, setCloudSourcesData, setSelectedCloudSourceRows]);

  const openSourceDialog = (
    dialogMode: SourceDialogMode = SourceDialogMode.New,
    device_uid: string = '',
    source_type_id?: number
  ) => {
    setMode(dialogMode);
    setDeviceId(device_uid);
    setConnectionInputSchema({});
    setSelectedIngestConfigTab('ingest_method_config');
    setCurrentStep(1);
    setWizardSelectedSource(null);
    if (
      dialogMode === SourceDialogMode.Edit ||
      dialogMode === SourceDialogMode.Delete
    ) {
      // Use passed source_type_id if available, otherwise fall back to atom value
      setSelectedSourceTypeId(
        source_type_id ?? selectedCloudSource.source_type_id
      );
      // Clear any stale source details to prevent old data from showing
      if (dialogMode === SourceDialogMode.Edit) {
        setSelectedSourceDetails({} as SourceDetailResponse);
      }
    } else {
      setSelectedSourceTypeId(0);
    }
    setOpen(true);
  };

  const closeSourceDialog = () => {
    setSelectedSourceDetails({} as SourceDetailResponse);
    setConnectionInputSchema({});
    setSourceRequest({} as SourceCreateBaseDTO);
    setOpen(false);
  };

  const refreshDeviceListing = () => {
    setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
  };

  const refreshCloudSources = () => {
    const sourceListKey = `organizations/${selectedOrganization.label}/lima_sources`;
    const activationEligibilityKey = `organizations/${selectedOrganization.label}/lima_sources/activation_eligibility`;

    queryClient.invalidateQueries({ queryKey: [sourceListKey] });
    queryClient.invalidateQueries({ queryKey: [activationEligibilityKey] });
  };

  /**
   * Extracts the meaningful error message from backend error responses.
   * Backend errors often include lengthy stack traces - we extract just the final message.
   *
   * @param err - Full error string from backend
   * @returns Clean error message without stack trace prefix
   */
  const extractErrorMessage = (err: string): string => {
    const match = err.match(/:\s*([^:]+)$/);
    return match ? match[1].trim() : err;
  };

  /**
   * Detects duplicate source errors and provides user-friendly guidance.
   *
   * Why this matters: Many source types require uniqueness constraints.
   * When a duplicate is detected, we guide users to use the 'site' field
   * to differentiate between multiple sources of the same type.
   */
  const checkForDuplicateSourceError = (err: string) => {
    if (err.toLowerCase().includes('already exists')) {
      setError(
        `A source for this organization already exists. Please use the 'site' field to differentiate.`
      );
    }
  };
  const mutation = useMutation({
    mutationFn: (src: SourceCreateBaseDTO) => {
      switch (mode) {
        case SourceDialogMode.Edit:
          return axiosClient.put(
            `/lima_sources/${selectedCloudSource.source_uid}`,
            src
          );
        case SourceDialogMode.AddThumbPrintId:
          return axiosClient.put(`/lima_sources/thumbprint`, {
            thumbprint_id: src.thumbprint_id,
            source_uid: src.source_uid,
            source_name: src.source_name
          });

        case SourceDialogMode.AddAccessCode:
          return axiosClient.put(`/lima_sources/access_code`, {
            access_code: src.access_code || '',
            source_uid: src.source_uid,
            source_name: src.source_name
          });

        case SourceDialogMode.Delete:
          return axiosClient.put(
            `/lima_sources/${selectedCloudSource.source_uid || 0}/delete_request`
          );
        default:
          return axiosClient.post('/lima_sources/', src);
      }
    },
    onSuccess: (data) => {
      if (data.status === 200 || data.status === 201) {
        setError('');
        if (isValidUUID(data?.data?.source_uid)) {
          setSourceResponse(data?.data as SourceCreateBaseDTO);
        }

        // Microsoft certificate authentication workflow:
        // 1. When using certificate auth for Microsoft sources, the system generates
        //    a self-signed certificate that must be uploaded to Azure AD
        // 2. After upload, user must retrieve the thumbprint ID from Azure and add it
        // 3. This flow handles automatic certificate download and dialog transition
        const isMicrosoftSource =
          selectedSourceTypeId === SOURCE_TYPE.O365 ||
          selectedSourceTypeId === SOURCE_TYPE.MICROSOFT_SENTINEL ||
          selectedSourceTypeId === SOURCE_TYPE.MICROSOFT_DEFENDER ||
          selectedSourceTypeId === SOURCE_TYPE.MICROSOFT_ENTRA_ID ||
          selectedSourceTypeId === SOURCE_TYPE.MICROSOFT;

        const shouldDownloadCertificate =
          authenticationMode === 'certificate' &&
          isMicrosoftSource &&
          newCertificate;

        // New source creation with certificate: Download cert and prompt for thumbprint
        if (mode === SourceDialogMode.New && shouldDownloadCertificate) {
          downloadCertificate(data?.data?.source_uid || '');
          openSourceDialog(SourceDialogMode.AddThumbPrintId);
        }
        // Editing source with certificate regeneration: Download new cert and prompt
        else if (
          mode === SourceDialogMode.Edit &&
          regenerateCertificate &&
          shouldDownloadCertificate
        ) {
          downloadCertificate(data?.data?.source_uid || '');
          openSourceDialog(SourceDialogMode.AddThumbPrintId);
        }
        // Dropbox-specific: Requires access code after source creation
        else if (
          mode === SourceDialogMode.New &&
          selectedSourceTypeId === SOURCE_TYPE.DROPBOX
        ) {
          openSourceDialog(SourceDialogMode.AddAccessCode);
        }
        // Standard flow: Close dialog and refresh
        else {
          closeSourceDialog();
          showSnackbar(mode, enqueueSnackbar);
          refreshCloudSources();
        }
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        const errMsg = extractErrorMessage(err.response?.data?.detail);
        setError(`Error saving source: ${errMsg}`);
        checkForDuplicateSourceError(err.response?.data?.detail);
      } else {
        const errMsg = extractErrorMessage(err.message);
        setError(`Error saving source: (${errMsg})`);
        checkForDuplicateSourceError(err.message);
      }
    }
  });

  const isEditMode = useCallback(() => {
    return mode === SourceDialogMode.Edit;
  }, [mode]);

  /**
   * Calculates which categories were removed during Edit mode.
   * Used to clean up log type status entries when categories are deselected.
   *
   * @param previousCategories - Categories that were previously selected
   * @param currentCategories - Categories currently selected
   * @returns Array of category names that were removed
   */
  const getRemovedCategories = useCallback(
    (previousCategories: string[], currentCategories: string[]) => {
      if (!isEditMode()) return [];

      return previousCategories.filter(
        (category) => !currentCategories.includes(category)
      );
    },
    [isEditMode]
  );

  /**
   * Calculates which regions were removed during Edit mode.
   * Used to clean up log type status entries when regions are deselected.
   *
   * @param previousRegions - Regions that were previously selected
   * @param currentRegions - Regions currently selected
   * @returns Array of region names that were removed
   */
  const getRemovedRegions = useCallback(
    (previousRegions: string[], currentRegions: string[]) => {
      if (!isEditMode()) return [];

      return previousRegions.filter(
        (region) => !currentRegions.includes(region)
      );
    },
    [isEditMode]
  );

  /**
   * Identifies newly added categories during Edit mode.
   *
   * Why this matters: Certain source types (Cisco Duo, Microsoft Defender, CrowdStrike,
   * Proofpoint, Juniper Mist) support incremental category addition. Each new category
   * triggers a separate ingestion job.
   *
   * @param previousCategories - Categories that existed before the edit
   * @param currentCategories - Categories after user modifications
   * @param sourceTypeId - Source type ID to determine if incremental add is supported
   * @returns Array of newly added category names
   */
  const getNewlyAddedCategories = useCallback(
    (
      previousCategories: string[],
      currentCategories: string[],
      sourceTypeId: number
    ) => {
      if (!isEditMode()) return [];

      // Only these source types support incremental category addition
      const supportsIncrementalCategories = [
        SOURCE_TYPE.CISCO_DUO,
        SOURCE_TYPE.MICROSOFT_DEFENDER,
        SOURCE_TYPE.CROWD_STRIKE,
        SOURCE_TYPE.PROOFPOINT_TAP,
        SOURCE_TYPE.PROOFPOINT_POD,
        SOURCE_TYPE.JUNIPER_MIST
      ].includes(sourceTypeId);

      if (!supportsIncrementalCategories) return [];

      return currentCategories.filter(
        (category) => !previousCategories.includes(category)
      );
    },
    [isEditMode]
  );

  /**
   * Identifies newly added regions during Edit mode.
   *
   * AWS CloudTrail: CloudTrail supports adding new regions without full re-ingestion.
   * Each region tracks its own trail and event history independently.
   *
   * @param previousRegions - Regions configured before the edit
   * @param currentRegions - Regions after user modifications
   * @param sourceTypeId - Source type ID (only AWS CloudTrail supported)
   * @returns Array of newly added region names
   */
  const getNewlyAddedRegions = useCallback(
    (
      previousRegions: string[],
      currentRegions: string[],
      sourceTypeId: number
    ) => {
      if (!isEditMode()) return [];
      if (sourceTypeId !== SOURCE_TYPE.AWS_CLOUDTRAIL) return [];

      return currentRegions.filter(
        (region) => !previousRegions.includes(region)
      );
    },
    [isEditMode]
  );

  /**
   * Identifies newly added cloud providers during Edit mode.
   *
   * CrowdStrike: Supports multiple cloud provider integrations (AWS, Azure, GCP).
   * Each provider can be added independently to extend monitoring coverage.
   *
   * @param previousProviders - Cloud providers configured before the edit
   * @param currentProviders - Cloud providers after user modifications
   * @param sourceTypeId - Source type ID (only CrowdStrike supported)
   * @returns Array of newly added cloud provider names
   */
  const getNewlyAddedCloudProviders = useCallback(
    (
      previousProviders: string[],
      currentProviders: string[],
      sourceTypeId: number
    ) => {
      if (!isEditMode()) return [];
      if (sourceTypeId !== SOURCE_TYPE.CROWD_STRIKE) return [];

      return currentProviders.filter(
        (cloudProvider) => !previousProviders.includes(cloudProvider)
      );
    },
    [isEditMode]
  );

  /**
   * Clears log type status entries for removed categories/regions.
   *
   * Why needed: When editing a source and removing categories or regions,
   * the corresponding log type status entries must be deleted to prevent
   * orphaned ingestion tracking data.
   */
  const deleteLogTypeStatusMutation = useMutation({
    mutationFn: ({
      sourceUid,
      logTypes
    }: {
      sourceUid: string;
      logTypes: string[];
    }) => {
      return axiosClient.delete(
        `/lima_sources/${sourceUid}/lima_log_type_status/clear`,
        {
          data: logTypes
        }
      );
    }
  });

  /**
   * Triggers manual re-ingestion of source data.
   *
   * Use cases:
   * - Source configuration was fixed after initial failure
   * - Historical data needs to be re-processed
   * - Testing ingestion pipeline changes
   */
  const resubmitMutation = useMutation({
    mutationFn: (sourceUid: string) => {
      return axiosClient.put(`/lima_sources/${sourceUid}/resubmit/true`);
    },
    onSuccess: (data) => {
      if (data.status === 200 || data.status === 201) {
        showSnackbar('Resubmit', enqueueSnackbar);
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        setError(
          `${err?.response?.data?.detail || 'Error resubmitting source'}`
        );
      } else {
        setError(`Error resubmitting source (${err.message})`);
      }
    }
  });

  /**
   * Re-ingests a specific custom object (category) for a source.
   *
   * Why separate from resubmit: Some sources support many categories/objects.
   * When only one fails, we can re-ingest just that object instead of
   * re-processing the entire source.
   *
   * Example: Microsoft Defender with 10 categories - if "Alerts" fails,
   * resubmit only that category without affecting the other 9.
   */
  const resubmitCustomObjectMutation = useMutation({
    mutationFn: (payload: ResubmitCustomObjectRequest) => {
      return axiosClient.put(
        `/lima_sources/${payload.source_uid}/lima_log_type_status/${payload.custom_object}/resubmit`
      );
    },
    onSuccess: (data) => {
      if (data.status === 200 || data.status === 201) {
        showSnackbar('ResubmitCustomObject', enqueueSnackbar);
        setCustomEvent(CustomEventTypes.REFRESH_CLOUD_SOURCE_DETAIL);
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        setError(
          `${err?.response?.data?.detail || 'Error resubmitting source'}`
        );
      } else {
        setError(`Error resubmitting source (${err.message})`);
      }
    }
  });

  /**
   * Triggers on-demand vulnerability scanning for Medigate sources.
   *
   * Why special: Medigate vulnerability scans are resource-intensive and
   * run on-demand rather than continuously. The source tracks whether
   * a scan is currently running via the on_demand_vuln_scan flag.
   */
  const runIngestionMutation = useMutation({
    mutationFn: (payload: MedigateOndemandIngestionDTO) => {
      return axiosClient.put(`/lima_sources/run_ingestion`, payload);
    },
    onSuccess: (data) => {
      if (data.status === 200 || data.status === 201) {
        showSnackbar('Runingestion', enqueueSnackbar);
        const selSourceDetails = selectedSourceDetails;
        selSourceDetails.on_demand_vuln_scan = true;
        setSelectedSourceDetails(selSourceDetails);
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        setError(
          `${err?.response?.data?.detail || 'Error running ingestion submission'}`
        );
      } else {
        setError(`Error resubmitting source (${err.message})`);
      }
    }
  });

  /**
   * Bulk activates or deactivates multiple sources.
   *
   * Use cases:
   * - Temporarily pause ingestion for maintenance
   * - Disable sources for customers with expired contracts
   * - Re-enable sources after configuration fixes
   *
   * On success: Clears grid selections and refreshes the source list
   */
  const updateActivationToggleMutation = useMutation({
    mutationFn: (payload: ToggleActiveStatePayload) => {
      return axiosClient.put(`/lima_sources/update-activation-status`, payload);
    },
    onSuccess: (data, variables) => {
      if (data.status === 200 || data.status === 201) {
        resetSourceActivationSelection();
        showSnackbar(
          variables.active ? 'activated' : 'deactivated',
          enqueueSnackbar
        );
        refreshCloudSources();
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        setError(
          `${err?.response?.data?.detail || 'Error activate/deactivate source'}`
        );
      } else {
        setError(`Error activate/deactivate source (${err.message})`);
      }
    }
  });

  // Clear error messages when source request changes (user is actively editing)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError('');
  }, [sourceRequest]);

  return {
    sourceRequest,
    open,
    error,
    mutation,
    resubmitMutation,
    runIngestionMutation,
    resubmitCustomObjectMutation,
    updateActivationToggleMutation,
    mode,
    setError,
    openSourceDialog,
    closeSourceDialog,
    refreshDeviceListing,
    setSourceRequest,
    authenticationMode,
    newCertificate,
    setAuthenticationMode,
    setNewCertificate,
    regenerateCertificate,
    setRegenerateCertificate,
    sourceResponse,
    getNewlyAddedCategories,
    getNewlyAddedRegions,
    getNewlyAddedCloudProviders,
    getRemovedCategories,
    getRemovedRegions,
    deleteLogTypeStatusMutation
  };
};

/**
 * Centralized state accessor for source creation/editing.
 *
 * Purpose: Provides a single hook to access all Jotai atoms related to
 * source creation, avoiding scattered atom imports throughout components.
 *
 * State categories:
 * - Organization & Device context
 * - Selected source type and connection schema
 * - Form field values (categories, regions, cloud providers, etc.)
 * - Feature flags (vulnerability scanning, object fetching)
 * - UI state (selected tabs, wizard steps)
 */
export const useSourceCreateState = () => {
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [sourceTypeBasicData] = useAtom(sourceTypeBasicAtom);
  const [connectionInputSchema, setConnectionInputSchema] = useAtom(
    connectionInputSchemaAtom
  );
  const [selectedDevice] = useAtom(selectedDeviceAtom);
  const [selectedSourceTypeId, setSelectedSourceTypeId] = useAtom(
    selectedSourceTypeIdAtom
  );
  const [
    enableFetchDeviceVulnerabilities,
    setEnableFetchDeviceVulnerabilities
  ] = useAtom(enableVulScanAtom);
  const [enableFetchObjects, setEnableFetchObjects] = useAtom(
    enableFetchObjectsAtom
  );
  const [selectedSource] = useAtom(selectedSourceAtom);
  const [selectedCloudSource] = useAtom(selectedCloudSourceAtom);
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const [devices] = useAtom(devicesAtom);
  const [limaDevices] = useAtom(limaDevicesAtom);
  const [categoryMasterList, setCategoryMasterList] = useAtom(categoryAtom);
  const [sourceLogTypeData] = useAtom(sourceLogTypeAtom);
  const [sourceStatus, setSourceStatus] = useAtom(sourceStatusAtom);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useAtom(
    selectedSubscriptionTypeAtom
  );
  const [deviceId, setDeviceId] = useAtom(selectedDeviceIdAtom);
  const [categories, setCategories] = useAtom(selectedCategoryAtom);
  const [region, setRegion] = useAtom(selectedRegionAtom);
  const [selectedTimeInterval, setSelectedTimeInterval] = useAtom(
    selectedTimeIntervalAtom
  );
  const [selectedSubTimeInterval, setSelectedSubTimeInterval] = useAtom(
    selectedSubTimeIntervalAtom
  );
  const [subTimeIntervalOptions, setSubTimeIntervalOptions] = useAtom(
    subTimeIntervalOptionsAtom
  );
  const [selectedSubSourceType, setSelectedSubSourceType] = useAtom(
    selectedSubSourceTypeAtom
  );

  const [cloudProvider, setCloudProvider] = useAtom(selectedCloudProviderAtom);
  const [deviceAlertFields, setDeviceAlertFields] = useAtom(
    deviceAlertFieldsAtom
  );
  const [vulnFields, setVulnFields] = useAtom(selectedVulnFieldsAtom);
  const [selectedIngestConfigTab, setSelectedIngestConfigTab] = useAtom(
    selectedIngestConfigTabAtom
  );
  return {
    selectedOrgData,
    sourceTypeBasicData,
    connectionInputSchema,
    setConnectionInputSchema,
    selectedDevice,
    selectedSourceTypeId,
    setSelectedSourceTypeId,
    enableFetchDeviceVulnerabilities,
    setEnableFetchDeviceVulnerabilities,
    enableFetchObjects,
    setEnableFetchObjects,
    selectedSource,
    selectedCloudSource,
    selectedSourceDetails,
    devices,
    limaDevices,
    sourceLogTypeData,
    categoryMasterList,
    setCategoryMasterList,
    sourceStatus,
    setSourceStatus,
    selectedSubscriptionType,
    setSelectedSubscriptionType,
    deviceId,
    setDeviceId,
    categories,
    setCategories,
    region,
    setRegion,
    selectedSubTimeInterval,
    setSelectedSubTimeInterval,
    selectedTimeInterval,
    setSelectedTimeInterval,
    subTimeIntervalOptions,
    setSubTimeIntervalOptions,
    cloudProvider,
    setCloudProvider,
    deviceAlertFields,
    setDeviceAlertFields,
    vulnFields,
    setVulnFields,
    selectedSubSourceType,
    setSelectedSubSourceType,
    selectedIngestConfigTab,
    setSelectedIngestConfigTab
  };
};
